import {useCallback, useEffect, useRef, useState} from "react";
import auth from "../../../utils/authentication";
import {
    fetchNewFutureEpisodes,
    fetchNextEpisodes,
    fetchSquadflixAvailableItems,
    fetchTorrestreamItems,
    fetchTrendingMovies,
    fetchTrendingSeries
} from "../api/cap17drive-api";
import {TowersSportsApi} from "../../../apis/towers-sports-api/towers-sports-api";
import {LeagueEvent, StremioMeta} from "../../../apis/towers-sports-api/types";
import {SquadflixCollectionsApi} from "../../../apis/squadflix-collections/SquadflixCollectionsApi";
import {
    SquadflixCollectionPagination,
    SquadflixCollectionResp
} from "../../app/myProfileView/my-profile-subroutes/my-profile-lists/types";
import {isLeagueGameFinished} from "./useLiveChannelDetailsData";

export const useContinueWatchingData = () => {
    const [episodes, setEpisodes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth.currentUser) return;
        fetchNextEpisodes(auth.currentUser.uid)
            .then(eps => {
                const now = new Date();
                const valid = (eps || []).filter((item: any) => {
                    if (!item.air_date) return true;
                    return new Date(item.air_date) <= now;
                });
                setEpisodes(valid);
            })
            .catch(() => setEpisodes([]))
            .finally(() => setLoading(false));
    }, []);

    return {episodes, loading};
};

export const useSpanishLeagueEventsData = () => {
    const [events, setEvents] = useState<LeagueEvent[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        TowersSportsApi.getEvents()
            .then(res => {
                const active = (res?.events || []).filter(ev => !isLeagueGameFinished(ev));
                setEvents(active);
            })
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, []);

    return {events, loading};
};

export const useNewEpisodesData = () => {
    const [series, setSeries] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth.currentUser) return;
        fetchNewFutureEpisodes(auth.currentUser.uid)
            .then(res => setSeries(res || []))
            .catch(() => setSeries([]))
            .finally(() => setLoading(false));
    }, []);

    return {series, loading};
};

export const useTrendingMoviesData = () => {
    const [movies, setMovies] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTrendingMovies()
            .then(data => setMovies(data?.results || []))
            .catch(() => setMovies([]))
            .finally(() => setLoading(false));
    }, []);

    return {movies, loading};
};

export const useTrendingSeriesData = () => {
    const [series, setSeries] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTrendingSeries()
            .then(data => setSeries(data?.results || []))
            .catch(() => setSeries([]))
            .finally(() => setLoading(false));
    }, []);

    return {series, loading};
};

export const useStremioChannelsData = () => {
    const [channels, setChannels] = useState<StremioMeta[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const getChannels = async (intent = 0) => {
            const eps = await TowersSportsApi.getAcestreamChannels().catch(() => null);
            if (!eps?.metas?.length && intent < 3) {
                setTimeout(() => {
                    if (mounted) getChannels(intent + 1);
                }, 1000);
                return;
            }
            if (mounted) {
                setChannels(eps?.metas || []);
                setLoading(false);
            }
        };
        getChannels();
        return () => {
            mounted = false;
        };
    }, []);

    return {channels, loading};
};

export const useSquadflixOriginalsData = () => {
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            fetchSquadflixAvailableItems({type: "series", mode: "squadflix"}),
            fetchSquadflixAvailableItems({type: "movies", mode: "squadflix"})
        ])
            .then(([series, movies]) => {
                const joined = [...(series?.results || []), ...(movies?.results || [])];
                joined.sort((a, b) => {
                    const aName = a.name || a.title || "";
                    const bName = b.name || b.title || "";
                    return aName.localeCompare(bName);
                });
                setItems(joined);
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    return {items, loading};
};

export const useTorrestreamItemsData = () => {
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth.currentUser) return;
        fetchTorrestreamItems(auth.currentUser.uid)
            .then(res => setItems(res?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    return {items, loading};
};

export const useCollectionItemsData = (collectionId: string) => {
    const [items, setItems] = useState<any[]>([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [pagination, setPagination] = useState<SquadflixCollectionPagination | undefined>(undefined);
    const loadedPages = useRef<Record<number, SquadflixCollectionPagination>>({});
    const itemsRef = useRef<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [collectionInfo, setCollectionInfo] = useState<SquadflixCollectionResp["collection"]>({} as any);

    const fetchPage = useCallback(async (page: number) => {
        setLoading(true);
        return SquadflixCollectionsApi.getCollectionDetails({collectionId, page})
            .then((resp: SquadflixCollectionResp) => {
                const pageData = resp?.collection?.result;
                if (pageData && !loadedPages.current[pageData.page]) {
                    loadedPages.current[pageData.page] = pageData;
                    itemsRef.current.push(
                        ...(pageData.items || []).map(x => ({
                            ...x.item,
                            isMovie: x.isMovie,
                            view: x.view
                        }))
                    );
                    setItems([...itemsRef.current]);
                }
                setPagination(pageData);
                setCollectionInfo(resp?.collection || ({} as any));
                setInitialLoading(false);
            })
            .catch(() => {
                setInitialLoading(false);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [collectionId]);

    useEffect(() => {
        loadedPages.current = {};
        itemsRef.current = [];
        setItems([]);
        setInitialLoading(true);
        fetchPage(1);
    }, [fetchPage]);

    const loadNextPage = useCallback(() => {
        if (loading || !pagination) return;
        if (pagination.page >= pagination.totalPages) return;
        fetchPage(pagination.page + 1);
    }, [loading, pagination, fetchPage]);

    return {
        items,
        collectionInfo,
        initialLoading,
        loading,
        loadNextPage
    };
};
